import React, {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { commitMutation, useLazyLoadQuery } from "react-relay";
import { useHistory, useParams } from "react-router-dom";

import type { GroupsState } from "..";
import { noop } from "lodash";
import { ConnectionHandler, graphql, type RecordProxy } from "relay-runtime";

import {
	changeActionItemsAttrsMutation,
	changeActionItemsStatusMutation,
	createActionItemsMutation,
	deleteActionItemsMutation,
} from "~/actionItems/ActionItem";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import type { GroupByType } from "~/actionItems/filters/GroupBy";
import type { SortByType } from "~/actionItems/filters/SortBy";
import type { AssigneeType } from "~/common/AssigneeFilter";
import { warning } from "~/lib/prompts";
import store from "~/lib/store";
import { isMobile } from "~/lib/utils";
import { environment } from "~/models";
import { useModals } from "~/ui";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import toasty from "~/ui/toast";

import { NavContext } from "../../nav";
import type { AssigneeStatusType } from "../../notes/AssignableDropdown";
import { ActionItemsTabContext } from "../ActionItemsTab";
import {
	ACTION_ITEMS_PANEL_SUFFIX,
	ACTION_ITEMS_STORE,
	ACTION_ITEMS_STORE_GROUP_BY,
	ACTION_ITEMS_STORE_HIDE_AI,
	ACTION_ITEMS_STORE_SHOW_AI_DETECTED,
	ACTION_ITEMS_STORE_SHOW_COMPLETED,
	ACTION_ITEMS_STORE_SHOW_CREATED_BY_YOU,
	ACTION_ITEMS_STORE_SHOW_RECENT_ONLY,
	ACTION_ITEMS_STORE_SHOW_WONT_DO,
	ACTION_ITEMS_STORE_SORT_BY,
	ACTION_ITEMS_STREAM_SUFFIX,
	ACTION_ITEMS_TEAMMATES_SUFFIX,
	ADDITIONAL_ITEMS_TO_RENDER_ON_EXPANSION,
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CUSTOM,
	GROUP_BY_NONE,
	INITIAL_ITEMS_TO_RENDER,
	SORT_BY_CUSTOM,
	SORT_BY_DUE_DATE,
	Views,
} from "../constants";
import type { FilterType } from "../filters/Filters";
import { composeItemsKeyString } from "../hooks/useActionItemsGroupItemsKey";
import { useSyncedPersistentState } from "../hooks/useSyncedPersistentState";
import NewActionItemModal from "../NewActionItemModal";
import { PlainTextQuotedItem } from "../QuotedItem";
import type { ActionItem, GroupType } from "../types";

import type { ActionItemChangeAttrsMutation } from "~/__generated__/ActionItemChangeAttrsMutation.graphql";
import type {
	ActionItemChangeStatusMutation,
	ToggleBlockFieldField,
} from "~/__generated__/ActionItemChangeStatusMutation.graphql";
import type { ActionItemCreateMutation } from "~/__generated__/ActionItemCreateMutation.graphql";
import type { ActionItemDeleteMutation } from "~/__generated__/ActionItemDeleteMutation.graphql";
import type { ActionItemsContextQuery } from "~/__generated__/ActionItemsContextQuery.graphql";

import styles from "./ActionItems.module.css";

const actionItemsContextQuery = graphql`
	query ActionItemsContextQuery {
		viewer {
			account {
				tags {
					__typename
					id
					label
					color
				}
				billingAccount {
					hasAccessToDuedates: hasAccessToFeature(feature: duedates)
					hasAccessToMultipleAssignees: hasAccessToFeature(feature: multiple_assignees)
				}
				allowCompleteByAny
			}
			user {
				id
				fullName
				image
				email
				internalAttributes {
					manager {
						id
					}
					directReports {
						edges {
							node {
								id
							}
						}
					}
				}
			}
		}
	}
`;

const useActionItemsContextQuery = () => {
	return useLazyLoadQuery<ActionItemsContextQuery>(actionItemsContextQuery, {});
};

export type ActionItemsRecordingContextType = {
	noteId: string;
	recordingId: string;
	videoDuration: number;
	onTimestampClick: ((timestamp: number) => void) | null;
};

export const ActionItemsRecordingContext = createContext<ActionItemsRecordingContextType>({
	noteId: "",
	recordingId: "",
	videoDuration: 0,
	onTimestampClick: null,
});

type RenderingContext = "default" | "actionItemsPanel" | "seriesPanel" | "recording";

export type ActionItemsContextType = {
	view: Views;
	filters: FilterType;
	itemBeingEdited: ActionItem | undefined;
	setItemBeingEdited?: React.Dispatch<React.SetStateAction<ActionItem | undefined>>;
	selectedActionItems: Array<ActionItem>;
	setSelectedActionItems?: React.Dispatch<React.SetStateAction<Array<ActionItem>>>;
	groupsState: GroupsState;
	setGroupsState: React.Dispatch<React.SetStateAction<GroupsState>>;
	// TODO: Add support for multiple assignee once we have it
	onCreateActionItem(groupId?: string, dueDate?: string, assignee?: UserAttrs): void;
	onMarkItemsAsCompleted(ids: Array<string>): void;
	onMarkItemsAsIncompleted(ids: Array<string>): void;
	onMarkItemsAsWontDo(ids: Array<string>): void;
	onUpdateItemsAttrs(items: Array<string>, updates: unknown): void;
	onDeleteItem(item: ActionItem): void;
	onDeleteItems(items: Array<string>): void;
	adjacentItemSelect: (item: ActionItem, groups: Array<GroupType>) => void;
	draggingId: string | undefined;
	setDraggingId: React.Dispatch<React.SetStateAction<string | undefined>>;
	tags: ActionItemsContextQuery["response"]["viewer"]["account"]["tags"];
	hasAccessToDueDates: boolean;
	viewer: ActionItemsContextQuery["response"]["viewer"] | null;
	onTeammatesView(): void;
	onUserView(): void;
	createActionItem: (content: string, groupId?: string, dueDate?: string, assignees?: UserAttrs[]) => void;
	inPanel: boolean;
	hasAccessToMultipleAssignees: boolean;
	allowCompleteByAny: boolean;
	renderingContext: RenderingContext;
};

export const ActionItemsContext = createContext<ActionItemsContextType>({
	view: Views.USER,
	filters: {
		groupBy: GROUP_BY_CUSTOM,
		onChangeGroupBy: noop,
		sortBy: SORT_BY_CUSTOM,
		onChangeSortBy: noop,
		showCompleted: false,
		onToggleShowCompleted: noop,
		showWontDo: false,
		onToggleShowWontDo: noop,
		showCreatedByYou: false,
		onToggleShowCreatedByYou: noop,
		showRecentOnly: true,
		onToggleShowRecentOnly: noop,
		showAIDetected: true,
		onToggleShowAIDetected: noop,
		hideAI: false,
		onToggleHideAI: noop,
		assignedTo: [],
		onChangeAssignedTo: noop,
		search: "",
		onChangeSearch: noop,
	},
	itemBeingEdited: undefined,
	setItemBeingEdited: noop,
	selectedActionItems: [],
	setSelectedActionItems: noop,
	groupsState: {},
	setGroupsState: noop,
	onCreateActionItem: noop,
	onMarkItemsAsCompleted: noop,
	onMarkItemsAsIncompleted: noop,
	onMarkItemsAsWontDo: noop,
	onUpdateItemsAttrs: noop,
	onDeleteItem: noop,
	onDeleteItems: noop,
	adjacentItemSelect: noop,
	draggingId: undefined,
	setDraggingId: noop,
	tags: [],
	hasAccessToDueDates: false,
	viewer: null,
	onTeammatesView: noop,
	onUserView: noop,
	createActionItem: noop,
	inPanel: false,
	hasAccessToMultipleAssignees: false,
	allowCompleteByAny: false,
	renderingContext: "default",
});

const actionItemsStore = store.namespace(ACTION_ITEMS_STORE);
const BASE_URL = "/action-items/";

/**
 * ActionItemsState manages the state for action items with a three-level override cascade:
 *
 * 1. Props: Values provided via the `filters` prop take highest precedence
 * 2. LocalStorage: If no prop is provided, values are loaded from localStorage (via store namespace)
 * 3. Defaults: If neither props nor localStorage has a value, default values are used
 *
 * State is synchronized with localStorage for persistence across sessions and
 * shared via context with child components. State depends on the current view:
 * - USER: Action items assigned to the current user
 * - TEAMMATES: Action items assigned to other users
 * - STREAM_TAB: Action items in the current stream
 * - RECORDING: Action items in the current recording
 *
 * Some state (group by, sort by) is specific to each view and panel combination,
 * while filter state is shared across all views to maintain consistent filtering.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} [props.filters] - Initial filter values (takes precedence over localStorage)
 * @param {string} [props.namespace="default"] - Namespace for localStorage keys, used to isolate state between different instances
 * @param {RenderingContext} [props.renderingContext] - Rendering context, used to render different UIs between different contexts
 */
export function ActionItemsState({
	children,
	filters,
	namespace = "default",
	...rest
}: PropsWithChildren<{
	filters?: Partial<
		Pick<
			FilterType,
			| "showCompleted"
			| "showAIDetected"
			| "hideAI"
			| "showWontDo"
			| "showRecentOnly"
			| "showCreatedByYou"
			| "assignedTo"
			| "search"
		>
	>;
	namespace?: string;
	renderingContext?: RenderingContext;
}>) {
	const { viewer } = useActionItemsContextQuery();
	const { streamId } = useContext(ActionItemsTabContext);
	const { noteId, recordingId } = useContext(ActionItemsRecordingContext);
	const { panel } = useContext(NavContext);
	const { view: routeView } = useParams<{ view: string }>();
	const history = useHistory();
	const initialView = useMemo(() => {
		if (streamId) return Views.STREAM_TAB;
		if (noteId && recordingId) return Views.RECORDING;
		return routeView && routeView.toLowerCase() === "teammates" ? Views.TEAMMATES : Views.USER;
	}, [routeView, streamId, noteId, recordingId]);
	const { user } = viewer;
	const modals = useModals();
	const [view, setView] = useState<Views>(initialView);
	const storeViewSuffix = useMemo(() => {
		if (view === Views.STREAM_TAB) return ACTION_ITEMS_STREAM_SUFFIX;
		if (view === Views.TEAMMATES) return ACTION_ITEMS_TEAMMATES_SUFFIX;
		return "";
	}, [view]);
	const inPanel = useMemo(() => panel.key === "action-items", [panel]);

	const renderingContext = useMemo(() => {
		// If a rendering context is provided, use it
		if (rest.renderingContext) return rest.renderingContext;

		// Existing logic for other contexts
		if (panel.key === "action-items") return "actionItemsPanel";
		if (noteId && recordingId) return "recording";
		return "default";
	}, [panel, rest.renderingContext, noteId, recordingId]);

	// Create a namespace prefix for localStorage keys
	const namespacePrefix = useMemo(() => {
		return namespace !== "default" ? `${namespace}_` : "";
	}, [namespace]);

	const storeSuffix = useMemo(() => {
		// Use empty suffix for filter-related state to sync filters across all views and panels
		// This ensures that filter preferences are shared between all views (user, teammates, stream tab) and panels
		return "";
	}, []);
	const storeSuffixWithPanel = useMemo(() => {
		// For group by and sort by, we want to maintain separate state between different views
		// and between panel and main view since these affect the UI layout differently
		return `${storeViewSuffix}${inPanel ? ACTION_ITEMS_PANEL_SUFFIX : ""}`;
	}, [storeViewSuffix, inPanel]);
	const [groupBy, setGroupBy] = actionItemsStore.useState<GroupByType>(
		`${namespacePrefix}${ACTION_ITEMS_STORE_GROUP_BY}${storeSuffixWithPanel}`,
		view !== Views.USER ? GROUP_BY_ASSIGNEE : GROUP_BY_CUSTOM,
	);
	const [sortBy, setSortBy] = actionItemsStore.useState<SortByType>(
		`${namespacePrefix}${ACTION_ITEMS_STORE_SORT_BY}${storeSuffixWithPanel}`,
		view !== Views.USER ? SORT_BY_DUE_DATE : SORT_BY_CUSTOM,
	);
	const [search, setSearch] = useState<string>(filters?.search || "");
	const [showCompleted, setShowCompleted] = useSyncedPersistentState<boolean>(
		actionItemsStore,
		`${namespacePrefix}${ACTION_ITEMS_STORE_SHOW_COMPLETED}${storeSuffix}`,
		filters?.showCompleted !== undefined ? filters.showCompleted : false,
	);
	const [showWontDo, setShowWontDo] = useSyncedPersistentState<boolean>(
		actionItemsStore,
		`${namespacePrefix}${ACTION_ITEMS_STORE_SHOW_WONT_DO}${storeSuffix}`,
		filters?.showWontDo !== undefined ? filters.showWontDo : false,
	);
	const [showCreatedByYou, setShowCreatedByYou] = useSyncedPersistentState<boolean>(
		actionItemsStore,
		`${namespacePrefix}${ACTION_ITEMS_STORE_SHOW_CREATED_BY_YOU}${storeSuffix}`,
		filters?.showCreatedByYou !== undefined ? filters.showCreatedByYou : false,
	);
	const [showRecentOnly, setShowRecentOnly] = useSyncedPersistentState<boolean>(
		actionItemsStore,
		`${namespacePrefix}${ACTION_ITEMS_STORE_SHOW_RECENT_ONLY}${storeSuffix}`,
		filters?.showRecentOnly !== undefined ? filters.showRecentOnly : true,
	);

	// This one is for server side filtering
	const [showAIDetected, setShowAIDetected] = useSyncedPersistentState<boolean>(
		actionItemsStore,
		`${namespacePrefix}${ACTION_ITEMS_STORE_SHOW_AI_DETECTED}${storeSuffix}`,
		filters?.showAIDetected !== undefined ? filters.showAIDetected : true,
	);

	// This one is for client side filtering so we dont have to re-fetch the items in the recap
	const [hideAI, setHideAI] = useSyncedPersistentState<boolean>(
		actionItemsStore,
		`${namespacePrefix}${ACTION_ITEMS_STORE_HIDE_AI}${storeSuffix}`,
		filters?.hideAI !== undefined ? filters.hideAI : false,
	);
	const [assignedTo, setAssignedTo] = useState<AssigneeType[] | undefined>(filters?.assignedTo || []);
	const [itemBeingEdited, setItemBeingEdited] = useState<ActionItem>();
	const [selectedActionItems, setSelectedActionItems] = useState<Array<ActionItem>>([]);
	const [groupsState, setGroupsState] = useState<GroupsState>({});
	const [draggingId, setDraggingId] = useState<string>();

	const onClick = useCallback((e: MouseEvent) => {
		if (e.defaultPrevented) return true;
		if (!(e.target instanceof Element)) return true;
		if (e.target.closest("[data-reach-dialog-overlay]")) return true;
		const itemElement = e.target.closest("[data-is-action-item]");
		const blockEditor = e.target.closest("[data-block-text-editor]");
		const actions = e.target.closest("[data-action-item-actions]");
		const actionGroup = e.target.closest("[data-is-action-item-group]");
		if (itemElement || blockEditor || actions || actionGroup) return true;
		setSelectedActionItems([]);
		return true;
	}, []);

	useEffect(() => {
		document.addEventListener("click", onClick);
		return () => document.removeEventListener("click", onClick);
	}, [onClick]);

	const onTeammatesView = useCallback(() => {
		setView(Views.TEAMMATES);
		setGroupBy(
			actionItemsStore.get(
				`${namespacePrefix}${ACTION_ITEMS_STORE_GROUP_BY}${ACTION_ITEMS_TEAMMATES_SUFFIX}${
					inPanel ? ACTION_ITEMS_PANEL_SUFFIX : ""
				}`,
			) || GROUP_BY_ASSIGNEE,
		);
		setSortBy(
			actionItemsStore.get(
				`${namespacePrefix}${ACTION_ITEMS_STORE_SORT_BY}${ACTION_ITEMS_TEAMMATES_SUFFIX}${
					inPanel ? ACTION_ITEMS_PANEL_SUFFIX : ""
				}`,
			) || SORT_BY_DUE_DATE,
		);
		if (!inPanel) history.replace(`${BASE_URL}teammates`);
	}, [history, setGroupBy, setSortBy, inPanel, namespacePrefix]);

	const onUserView = useCallback(() => {
		setView(Views.USER);
		setGroupBy(
			actionItemsStore.get(
				`${namespacePrefix}${ACTION_ITEMS_STORE_GROUP_BY}${inPanel ? ACTION_ITEMS_PANEL_SUFFIX : ""}`,
			) || GROUP_BY_CUSTOM,
		);
		setSortBy(
			actionItemsStore.get(
				`${namespacePrefix}${ACTION_ITEMS_STORE_SORT_BY}${inPanel ? ACTION_ITEMS_PANEL_SUFFIX : ""}`,
			) || SORT_BY_CUSTOM,
		);
		if (!inPanel) history.replace(BASE_URL);
	}, [history, setGroupBy, setSortBy, inPanel, namespacePrefix]);

	const onChangeGroupBy = useCallback(
		(newGroupBy: GroupByType) => {
			setGroupBy(newGroupBy);
			// Custom sorting is only supported on custom and none groups
			if (sortBy === SORT_BY_CUSTOM && newGroupBy !== GROUP_BY_CUSTOM && newGroupBy !== GROUP_BY_NONE) {
				setSortBy(SORT_BY_DUE_DATE);
			}
			setSelectedActionItems([]);
		},
		[sortBy, setGroupBy, setSortBy],
	);

	const onChangeSortBy = useCallback(
		(newSortBy: SortByType) => {
			setSortBy(newSortBy);
			setSelectedActionItems([]);
		},
		[setSortBy],
	);

	const onToggleShowCompleted = useCallback(
		() => setShowCompleted(showCompleted => !showCompleted),
		[setShowCompleted],
	);
	const onToggleShowWontDo = useCallback(() => setShowWontDo(showWontDo => !showWontDo), [setShowWontDo]);
	const onToggleShowCreatedByYou = useCallback(
		() => setShowCreatedByYou(showCreatedByYou => !showCreatedByYou),
		[setShowCreatedByYou],
	);
	const onToggleShowRecentOnly = useCallback(
		() => setShowRecentOnly(showRecentOnly => !showRecentOnly),
		[setShowRecentOnly],
	);

	const onToggleShowAIDetected = useCallback(
		() => setShowAIDetected(showAIDetected => !showAIDetected),
		[setShowAIDetected],
	);

	const onToggleHideAI = useCallback(() => setHideAI(hideAI => !hideAI), [setHideAI]);
	const onChangeAssignedTo = useCallback((newAssignedTo?: AssigneeType[]) => {
		setAssignedTo(newAssignedTo);
	}, []);
	const onChangeSearch = useCallback((newSearch: string) => setSearch(newSearch), []);

	const createActionItem = useCallback(
		(
			content: string,
			groupId?: string,
			dueDate?: string,
			assignees?: UserAttrs[],
			statusCondition?: StatusCondition,
		) => {
			const assigneeIds = assignees?.map(a => a.id);
			commitMutation<ActionItemCreateMutation>(environment, {
				mutation: createActionItemsMutation,
				variables: {
					input: {
						groupId,
						content,
						dueDate,
						assigneeIds,
						statusCondition,
					},
				},
				onCompleted: response => {
					if (!response.createTodo?.block) {
						toasty.error("Failed when creating the action item.");
						return;
					}
					if (!isMobile) toasty.success("New action item created.");
				},
				updater: (store, response) => {
					if (!response) return;
					const newBlock = response.createTodo?.block;
					if (!newBlock) {
						return;
					}
					const rootRecord = store.getRoot();
					if (!rootRecord) {
						return;
					}
					let shouldInsertRecord = true;
					if (view === Views.USER) {
						shouldInsertRecord = Boolean(!assigneeIds || (user?.id && assigneeIds.includes(user.id)));
					} else if (view === Views.TEAMMATES) {
						shouldInsertRecord = Boolean(assigneeIds && user?.id && !assigneeIds?.includes(user.id));
					}
					if (!shouldInsertRecord) return;
					let selectedGroupItemConnection: RecordProxy | undefined;
					const actionItemGroupsRecord = ConnectionHandler.getConnection(
						rootRecord,
						"ActionItemGroupsList_actionItemGroups",
					);
					if (actionItemGroupsRecord) {
						const actionItemGroups = (actionItemGroupsRecord.getLinkedRecords("edges") ?? []).map(e =>
							e.getLinkedRecord("node"),
						);
						for (let i = 0; i < actionItemGroups.length; i++) {
							// If we have a defined group we look for it
							// If not we look for the default one
							if (
								(groupId && actionItemGroups[i]?.getValue("id") === groupId) ||
								(!groupId && actionItemGroups[i]?.getValue("isDefaultGroup"))
							) {
								selectedGroupItemConnection = actionItemGroups[i]?.getLinkedRecord(
									composeItemsKeyString({ showCompleted, showWontDo, showAIDetected }),
								);
								break;
							}
						}
					}
					const syntheticConnectionRecord = ConnectionHandler.getConnection(
						rootRecord,
						"SyntheticActionItemGroupsList_actionItems",
						{
							includeCompleted: showCompleted,
							includeWontDo: showWontDo,
							includeAiDetected: showAIDetected,
							includeOnlyCreatedByYou: view !== Views.TEAMMATES ? undefined : showCreatedByYou,
							includeRecentOnly: view !== Views.TEAMMATES ? undefined : showRecentOnly,
							teammatesView: view === Views.TEAMMATES,
							assignedTo: view !== Views.TEAMMATES ? undefined : assignedTo?.map(assignee => assignee.user?.id || ""),
							forStream: view !== Views.STREAM_TAB ? undefined : streamId,
						},
					);
					const actionItemRecord = store.get(newBlock.id);
					if (!actionItemRecord) {
						return;
					}
					// Add item to the default group
					if (selectedGroupItemConnection) {
						const edge = ConnectionHandler.createEdge(
							store,
							selectedGroupItemConnection,
							actionItemRecord,
							"NoteBlock",
						);
						ConnectionHandler.insertEdgeBefore(selectedGroupItemConnection, edge);
					}
					// Add item to synthetic action items list
					if (syntheticConnectionRecord) {
						const edge = ConnectionHandler.createEdge(store, syntheticConnectionRecord, actionItemRecord, "NoteBlock");
						ConnectionHandler.insertEdgeBefore(syntheticConnectionRecord, edge);
					}
				},
			});
		},
		[showCompleted, showWontDo, showAIDetected, showCreatedByYou, showRecentOnly, view, assignedTo, streamId, user],
	);

	const onCreateActionItem = useCallback(
		(groupId?: string, dueDate?: string, assignee?: UserAttrs) => {
			modals.open(({ close }) => (
				<NewActionItemModal
					user={user}
					hasAccessToDueDates={viewer.account.billingAccount.hasAccessToDuedates}
					hasAccessToMultipleAssignees={viewer.account.billingAccount.hasAccessToMultipleAssignees}
					allowCompleteByAny={viewer.account.allowCompleteByAny}
					onClose={close}
					createItem={createActionItem}
					groupId={groupId}
					dueDate={dueDate}
					assignees={assignee ? [assignee] : user ? [user] : []}
				/>
			));
		},
		[modals, viewer, createActionItem, user],
	);

	const onChangeItemsStatus = useCallback((ids: Array<string>, field: ToggleBlockFieldField, value: boolean) => {
		const blockIds = ids.map(id => ({
			id,
		}));
		commitMutation<ActionItemChangeStatusMutation>(environment, {
			mutation: changeActionItemsStatusMutation,
			variables: {
				input: { field, ids: blockIds, value },
			},
			optimisticUpdater: store => {
				ids.forEach(id => {
					const node = store.get(id);
					node?.setValue(value, field === "DONE" ? "done" : "wontDo");
				});
			},
		});
	}, []);

	const onMarkItemsAsCompleted = useCallback(
		(ids: Array<string>) => {
			onChangeItemsStatus(ids, "DONE", true);
		},
		[onChangeItemsStatus],
	);

	const onMarkItemsAsIncompleted = useCallback(
		(ids: Array<string>) => {
			onChangeItemsStatus(ids, "DONE", false);
		},
		[onChangeItemsStatus],
	);

	const onMarkItemsAsWontDo = useCallback(
		(ids: Array<string>) => {
			onChangeItemsStatus(ids, "WONT_DO", true);
		},
		[onChangeItemsStatus],
	);

	const onUpdateItemsAttrs = useCallback((ids: Array<string>, updates: unknown) => {
		return commitMutation<ActionItemChangeAttrsMutation>(environment, {
			mutation: changeActionItemsAttrsMutation,
			variables: {
				input: {
					ids,
					updates,
				},
			},
			optimisticUpdater: store => {
				// When updating the assignees we want to locally update the new status so changes to complete by all
				// Blocks are reflected
				// @ts-ignore
				const {
					assignees,
					statusCondition,
				}: { assignees?: Array<AssigneeStatusType>; statusCondition?: StatusCondition } = updates;
				ids.forEach(id => {
					const actionItemRecord = store.get(id);
					if (!actionItemRecord) return;
					if (statusCondition) {
						actionItemRecord.setValue(statusCondition, "statusCondition");
					}
					if (!assignees || assignees.length <= 1) {
						actionItemRecord.setValue(StatusCondition.SINGLE, "statusCondition");
					}
					if (!assignees) return;

					const assigneesStatusMap = new Map<string, NoteBlockStatus>();
					// Let's check if the overall block status will change
					// If it is completed by all
					if (actionItemRecord.getValue("statusCondition") === StatusCondition.ALL) {
						const currentBlockStatus = actionItemRecord.getValue("done")
							? NoteBlockStatus.DONE
							: actionItemRecord.getValue("wontDo")
								? NoteBlockStatus.WONT_DO
								: NoteBlockStatus.NOT_DONE;
						let newAssigneeBasedStatus = NoteBlockStatus.DONE;
						let wontDoAmount = 0;
						assignees.forEach(assignee => {
							if (assignee.status !== NoteBlockStatus.DONE) {
								newAssigneeBasedStatus = NoteBlockStatus.NOT_DONE;
							}
							if (assignee.status === NoteBlockStatus.WONT_DO) {
								wontDoAmount++;
							}
							// We take this opportunity to fill the assignee status map
							assigneesStatusMap.set(assignee.id, assignee.status);
						});
						if (wontDoAmount === assignees.length) {
							newAssigneeBasedStatus = NoteBlockStatus.WONT_DO;
						}
						if (currentBlockStatus !== newAssigneeBasedStatus) {
							const done = newAssigneeBasedStatus === NoteBlockStatus.DONE;
							const wontDo = newAssigneeBasedStatus === NoteBlockStatus.WONT_DO;
							actionItemRecord.setValue(done, "done");
							actionItemRecord.setValue(wontDo, "wontDo");
						}
					}

					const assignmentRecords = actionItemRecord.getLinkedRecords("assignments");
					if (!assignmentRecords) return;
					// Assignee status map might be filled when checking the new note block status above
					// In that case we don't want to iterate over the assignee array twice
					if (assigneesStatusMap.size === 0) {
						assignees.forEach(assignee => assigneesStatusMap.set(assignee.id, assignee.status));
					}
					assignmentRecords.forEach(assignmentRecord => {
						const userRecord = assignmentRecord.getLinkedRecord("assignee");
						if (!userRecord || !assigneesStatusMap.has(userRecord.getDataID())) return;
						const newAssigneeStatus = assigneesStatusMap.get(userRecord.getDataID());
						if (newAssigneeStatus === assignmentRecord.getValue("status")) return;
						assignmentRecord.setValue(newAssigneeStatus, "status");
					});
				});
			},
		});
	}, []);

	const deleteItems = useCallback((ids: Array<string>) => {
		setSelectedActionItems([]);
		if (ids.length === 0) return;
		commitMutation<ActionItemDeleteMutation>(environment, {
			mutation: deleteActionItemsMutation,
			variables: {
				input: {
					ids,
				},
			},
			updater: store => {
				ids.forEach(ids => {
					store.delete(ids);
				});
			},
		});
	}, []);

	const onDeleteItems = useCallback(
		async (ids: Array<string>) => {
			const res = await warning(<></>, {
				title: `Delete (${ids.length}) action items?`,
				className: styles.deleteBody,
				yesProps: { variant: "destructive" },
				yesText: "Delete",
				noHeaderBorder: true,
			});
			if (!res) return;
			deleteItems(ids);
		},
		[deleteItems],
	);

	const onDeleteItem = useCallback(
		async (item: ActionItem) => {
			const res = await warning(
				<div className={styles.deleteModal}>
					You are deleting this action item from &quot;{item.note.stream.displayName || "Action items"}&quot;:
					<PlainTextQuotedItem
						text={item.text}
						done={item.done}
						type="action-item"
						streamName={item.note?.stream.displayName || ""}
						showAsLinked={item.hasOriginalBlock}
					/>
				</div>,
				{
					title: "Delete action item?",
					className: styles.deleteBody,
					yesProps: { variant: "destructive" },
					yesText: "Delete",
					noHeaderBorder: true,
				},
			);
			if (!res) return;
			deleteItems([item.id]);
		},
		[deleteItems],
	);

	const adjacentItemSelect = useCallback(
		(item: ActionItem, groups: Array<GroupType>) => {
			if (selectedActionItems.length === 0) {
				setSelectedActionItems([item]);
				return;
			}
			let fromItemIndex = -1;
			let toItemIndex = -1;
			let fromGroupIndex = -1;
			let toGroupIndex = -1;
			for (let i = 0; i < groups.length; i++) {
				let foundFrom = false;
				let foundTo = false;
				for (let j = 0; j < groups[i].items.edges.length; j++) {
					if (groups[i].items.edges[j]?.node.id === selectedActionItems[0].id) {
						fromItemIndex = j;
						foundFrom = true;
					} else if (groups[i].items.edges[j]?.node.id === item.id) {
						toItemIndex = j;
						foundTo = true;
					}
					if (fromItemIndex >= 0 && toItemIndex >= 0) {
						break;
					}
				}
				if (foundFrom) {
					fromGroupIndex = i;
				}
				if (foundTo) {
					toGroupIndex = i;
				}
				if (fromGroupIndex >= 0 && toGroupIndex >= 0) {
					break;
				}
			}
			let direction = 1;
			// Check if we are trying to adjance select items that are on top of the first selected item
			if (fromGroupIndex > toGroupIndex || (fromGroupIndex === toGroupIndex && fromItemIndex > toItemIndex)) {
				direction = -1;
			}
			let currentItemIndex = fromItemIndex;
			let currentGroupIndex = fromGroupIndex;
			const newSelectedItems = [];
			while (
				currentItemIndex !== toItemIndex ||
				currentGroupIndex !== toGroupIndex ||
				currentGroupIndex < 0 ||
				currentGroupIndex >= groups.length
			) {
				const amountBeingRendered =
					INITIAL_ITEMS_TO_RENDER +
					(groupsState[groups[currentGroupIndex].id]
						? groupsState[groups[currentGroupIndex].id].expansions * ADDITIONAL_ITEMS_TO_RENDER_ON_EXPANSION
						: 0);
				// We go to the next group if
				// Direction is bigger than 0 AND one of the following:
				// - We've past the end of the list
				// - The group is collapsed
				// - The group is not fully expanded and we've reached the end of the non expanded list
				const goToNextGroup =
					direction > 0 &&
					(groups[currentGroupIndex].items.edges?.length <= currentItemIndex ||
						(groupsState[groups[currentGroupIndex].id] && groupsState[groups[currentGroupIndex].id].collapsed) ||
						currentItemIndex >= amountBeingRendered);

				if (goToNextGroup) {
					currentGroupIndex += 1;
					if (currentGroupIndex >= groups.length) {
						break;
					}
					currentItemIndex = 0;
					continue;
				}

				// We go to the previous group if
				// Direction is smaller than 0 AND one of the following:
				// - We've past the start of the list
				// - The group is collapsed
				const goToPreviousGroup =
					direction < 0 &&
					(currentItemIndex < 0 ||
						(groupsState[groups[currentGroupIndex].id] && groupsState[groups[currentGroupIndex].id].collapsed));

				if (goToPreviousGroup) {
					currentGroupIndex -= 1;
					if (currentGroupIndex < 0 || !groupsState[groups[currentGroupIndex].id]) {
						break;
					}
					const notShowingAllForNextGroup = amountBeingRendered < groups[currentGroupIndex].items.edges?.length;
					if (notShowingAllForNextGroup) {
						currentItemIndex =
							groups[currentGroupIndex].items.edges?.length < amountBeingRendered
								? groups[currentGroupIndex].items.edges?.length - 1
								: amountBeingRendered - 1;
					} else {
						currentItemIndex = groups[currentGroupIndex].items.edges?.length - 1;
					}
					continue;
				}

				// Add item
				const currentGroupItems = groups[currentGroupIndex].items.edges;
				if (currentGroupItems) {
					// @ts-ignore
					newSelectedItems.push(currentGroupItems[currentItemIndex].node);
				}
				currentItemIndex += direction;
			}
			// Add the last item
			// @ts-ignore
			newSelectedItems.push(groups[toGroupIndex].items.edges[toItemIndex].node);
			setSelectedActionItems(Array.from(newSelectedItems));
			window.getSelection()?.empty();
			window.getSelection()?.removeAllRanges();
		},
		[selectedActionItems, groupsState],
	);

	const contextValues = useMemo<ActionItemsContextType>(() => {
		return {
			view,
			filters: {
				groupBy,
				onChangeGroupBy,
				sortBy,
				onChangeSortBy,
				showCompleted,
				onToggleShowCompleted,
				showWontDo,
				onToggleShowWontDo,
				showCreatedByYou,
				onToggleShowCreatedByYou,
				showRecentOnly,
				onToggleShowRecentOnly,
				showAIDetected,
				onToggleShowAIDetected,
				hideAI,
				onToggleHideAI,
				assignedTo,
				onChangeAssignedTo,
				search,
				onChangeSearch,
			},
			itemBeingEdited,
			setItemBeingEdited,
			selectedActionItems,
			setSelectedActionItems,
			groupsState,
			setGroupsState,
			onCreateActionItem,
			onMarkItemsAsCompleted,
			onMarkItemsAsIncompleted,
			onMarkItemsAsWontDo,
			onUpdateItemsAttrs,
			onDeleteItem,
			onDeleteItems,
			adjacentItemSelect,
			draggingId,
			setDraggingId,
			tags: viewer.account.tags,
			hasAccessToDueDates: viewer.account.billingAccount.hasAccessToDuedates,
			viewer,
			onTeammatesView,
			onUserView,
			createActionItem,
			inPanel,
			hasAccessToMultipleAssignees: viewer.account.billingAccount.hasAccessToMultipleAssignees,
			allowCompleteByAny: viewer.account.allowCompleteByAny,
			renderingContext,
		};
	}, [
		view,
		groupBy,
		onChangeGroupBy,
		sortBy,
		onChangeSortBy,
		showCompleted,
		onToggleShowCompleted,
		showWontDo,
		onToggleShowWontDo,
		showCreatedByYou,
		onToggleShowCreatedByYou,
		showRecentOnly,
		onToggleShowRecentOnly,
		showAIDetected,
		onToggleShowAIDetected,
		hideAI,
		onToggleHideAI,
		assignedTo,
		onChangeAssignedTo,
		search,
		onChangeSearch,
		itemBeingEdited,
		selectedActionItems,
		groupsState,
		onCreateActionItem,
		onMarkItemsAsCompleted,
		onMarkItemsAsIncompleted,
		onMarkItemsAsWontDo,
		onUpdateItemsAttrs,
		onDeleteItem,
		onDeleteItems,
		adjacentItemSelect,
		draggingId,
		viewer,
		onTeammatesView,
		onUserView,
		createActionItem,
		inPanel,
		renderingContext,
	]);

	return <ActionItemsContext.Provider value={contextValues}>{children}</ActionItemsContext.Provider>;
}
